import React, { useEffect, useState } from 'react';
import {
  Button,
  CheckMark,
  Icon,
  TranslationProvider,
} from '@resy/ui-kitchen';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import WidgetPage, { WidgetPageBody, WidgetPageHeader } from '../WidgetPage';
import OnScrollView from '../OnScrollView';
import WidgetNav from '../WidgetNav';
import WidgetTitle from '../WidgetTitle';
import './ReservationPathNaa.scss';
import { setBookingFlowState } from '../../../../../ui-kitchen/src/reducers/bookingSlice';
import { CONFIRMATION } from '../../../../../ui-kitchen/src/constants/bookingFlow';
import Link from '../../../../../ui-kitchen/src/components/Link';
import { Logger } from '../../../../../ui-kitchen/src/utils';
import { postAnnouncement, postOptimizelyTrackEvents } from '../../../../../ui-kitchen/src/api';
import reservationShape from '../ConfirmationPage/reservationShape';
import { useFunctionalCookie } from '../../../../../ui-kitchen/src/utils/resyUCMCookieUtils';
import announcementEvents from '../../../../../ui-kitchen/src/components/Announcement/announcementEvents';
import { getAmexPrefillToken } from '../../../../../ui-kitchen/src/api/api';
import { EVENT_TYPE } from '../../../../../ui-kitchen/src/components/Announcement/announcementTypes';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';

export const trackView = async ({
  announcementId, uuid, offerData,
}) => {
  try {
    await postAnnouncement({
      announcement_id: announcementId,
      uuid,
      eep_code: offerData.eep_code,
    });
  } catch (err) {
    Logger.error('[ReservationPathNaa] error posting tracking data to AMEX CTA link: ', err);
  }
};

const ReservationPathNaa = (props) => {
  const {
    Analytics, venueName, offerData, showBackButton, announcementId, uuid, setBookingFlow,
  } = props;
  const navigate = useNavigate();

  useFunctionalCookie(
    'uuid',
    uuid,
    {
      expires: moment().add(1, 'year').toDate(),
      path: '/',
    }
  );

  useEffect(() => {
    trackView({
      announcementId, uuid, offerData,
    });
  }, [announcementId]);

  const [titleOpacity, setTitleOpacity] = useState(0);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const onClickCTA = () => {
    setBookingFlow(CONFIRMATION);
  };

  const renderWidgetNav = () => {
    const { navSearch, reservation } = props;
    const { token } = reservation;
    const backPath = `/account/reservations/${token}/`;

    return (
      <WidgetNav
        Analytics={Analytics}
        backPath={backPath}
        navigate={navigate}
        pageContext="reservation path"
        showBack={showBackButton}
        showProfileMenu
        search={navSearch}
        titleOpacity={titleOpacity}
        venueName={venueName}
        type={showBackButton ? 'tile' : 'booking'}
        title={offerData?.header}
        eventName={announcementEvents.CLOSED}
      />
    );
  };

  const renderWidgetTitle = () => {
    const key = 'reservation_header';
    const fallback = 'Reservation Booked.';

    return (
      <WidgetTitle role="alert" horizontalRule={false}>
        <div className="ReservationPathNaa__container">
          <div className="ReservationPathNaa__header-content">
            <div className="ReservationPathNaa__header">
              <TranslationProvider translationKey={key}>
                <span>{fallback}</span>
              </TranslationProvider>
              <Icon component={CheckMark} name="check" />
            </div>
            <TranslationProvider translationKey="confirmation_check_inbox">
              <div className="WidgetTitle__subHeading">
                Please check your inbox for a confirmation email.
              </div>
            </TranslationProvider>
          </div>
          <TranslationProvider translationKey="continue_to_reservation">
            <Button
              className="Continue-button"
              sm
              variant="secondary"
              onClick={onClickCTA}
            >
              Continue to Reservation Details
            </Button>
          </TranslationProvider>
        </div>
      </WidgetTitle>
    );
  };

  const scrollFunc = (elems, scrollTop) => {
    const diffWithScrollTop = elems[0].offsetTop + elems[0].offsetHeight - scrollTop;
    const perc = 1 - diffWithScrollTop / elems[0].offsetHeight;

    setTitleOpacity(perc <= 0 ? 0 : 1);
  };

  const getScrollEvents = () => [
    {
      selector: ['.WidgetPageHeader'],
      func: scrollFunc,
    },
  ];

  const renderOfferDetails = (offerDetails) => offerDetails?.map((offer) => (
    <div className="offer-details" key={offer.sequence_number}>
      <Icon className="icon" url={offer.icon_url} aria-label="offer details icon" />
      <div className="offer-text">{offer.display_text}</div>
    </div>
  ));

  const addLoadingIndicator = (newTab) => {
    const keyframeCSS = `
      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `;

    // Create a <style> element
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.innerHTML = keyframeCSS;
    // Append the <style> element to the <head> of the document
    newTab.document?.head?.appendChild(styleElement);

    // Create a container <div>
    const loadingDiv = document.createElement('div');
    loadingDiv.setAttribute('id', 'loading-indicator');
    loadingDiv.style.display = 'flex';
    loadingDiv.style.justifyContent = 'center';
    loadingDiv.style.alignItems = 'center';
    loadingDiv.style.height = '100vh';

    //  Create <div> for spinner
    const spinningDiv = document.createElement('div');
    spinningDiv.setAttribute('id', 'spinning');
    spinningDiv.style.border = '8px solid #c3c3c3';
    spinningDiv.style.borderTop = '8px solid #00175a';
    spinningDiv.style.borderRadius = '50%';
    spinningDiv.style.width = '50px';
    spinningDiv.style.height = '50px';
    spinningDiv.style.animation = 'spin 1s linear infinite';
    //  Add spinner <div> into container <div>
    loadingDiv.appendChild(spinningDiv);
    //  Append container <div> to the <body> of the document
    newTab.document?.body?.appendChild(loadingDiv);
  };

  const learnMoreAndApplyNow = async (version) => {
    const { action_url: actionUrl, acquisition_offer_id: acquisitionOfferId } = offerData;
    //  Safari opens new tab with blank url, but its not getting the focus, only after a valid url is set,
    //  it brings to new tab to front. Due to this spinning animation is interrupted.
    //  This should fix the animation issue which is happening in safari web version.
    const isSafari = !!window.navigator.userAgent.match(/Version\/[\d.]+.*Safari/);
    const isiOS = /iP(ad|od|hone)/i.test(window.navigator.userAgent);
    /** *****************************************************************************************************************
     * Browsers don't allow to open a new tab/window programatically (without a user interaction) for security reasons.
     * Since we are fetching prefill token before the opening the new tab, browser will block any attempt to open a new
     * tab after completing the API call. To overcome this restriction, we are opening an empty tab immediately after
     * user clicking the button and then triggers the prefill API. Once the API finishes, using a unique name(time stamp
     * is used here for generating a uniqu name) getting the reference of initially opened tab and appends the form
     * element with required information. To indicate the user that the page is still loading while displaying the empty
     * page, a spinning indicator will be appended to the empty page.
    ******************************************************************************************************************* */
    const newTabUrl = ((isSafari && !isiOS) ? actionUrl : '');
    const tabName = Date.now().toString();
    const newTab = window.open(newTabUrl, tabName);
    if (!newTab) {
      window.alert('Please disable your pop-up blocker and try again.');
      return;
    }
    addLoadingIndicator(newTab);
    let prefillAmexDataResponse;
    try {
      setPrefillLoading(true);
      prefillAmexDataResponse = await getAmexPrefillToken();
      setPrefillLoading(false);
    } catch (err) {
      setPrefillLoading(false);
      Logger.error('[AmexPrefillToken] Error:', err);
    }

    const applicantRequestToken = prefillAmexDataResponse?.prefill_info?.applicant_request_token || '';

    Analytics.event(EVENT_TYPE.CLICK_ANNOUNCEMENT, {
      context: 'reservation path',
      type: showBackButton ? 'tile' : 'booking',
      title: offerData?.header,
      id: announcementId,
      cta: 'primary',
      variant: version,
    });
    try {
      postOptimizelyTrackEvents({
        slug: 'announcement_clicked',
        data: {
          eventTags: { version },
          track_user_id: true,
        },
      });
    } catch (err) {
      Logger.error('[OptimizelyTrackEvent] Error:', err);
    }
    const data = {
      applicant_request_token: applicantRequestToken,
      acq_experience: 'reservation',
      partner_page_name: 'reservationConfirmation',
      acquisition_offer_id: acquisitionOfferId,
      return_url: 'resy.com',
      channel: (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) // eslint-disable-line no-undef
        ? 'Mobile Web' : 'Desktop',
      partner: 'resy',
    };
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = actionUrl;
    form.target = tabName;

    Object.keys(data).forEach((key) => {
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = key;
      hiddenField.value = data[key];
      form.appendChild(hiddenField);
    });
    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const renderNAA = () => (
    <div className="NAA-container">
      <div className="NAA-image-wrapper">
        <img
          className="NAA-image"
          src={offerData?.image_url}
          alt="NAA-ad"
        />
      </div>
      <div className="NAA-content">
        <h4 className="NAA-subtitle">
          {offerData?.subheader}
        </h4>
        <div className="NAA-header">{offerData?.header}</div>
        {renderOfferDetails(offerData?.benefits)}
        <div className="NAA-conditions">
          <div className="annual-fees">
            {'Annual Fee: '}
            {offerData?.annual_fee_value}
          </div>
          &nbsp;|&nbsp;
          <Link
            className="rates-and-fees"
            href={offerData?.terms_and_fees_url}
            target="_blank"
          >
            Rates & Fees
          </Link>
          &nbsp;|&nbsp;
          <Link
            className="offer-and-benefits"
            href={offerData?.benefits_terms_url}
            target="_blank"
          >
            Offer & Benefit Terms
          </Link>
        </div>
        <div className="NAA-CTA-Wrapper">
          <Button
            className="NAA-CTA"
            variant="primary"
            processing={prefillLoading}
            onClick={() => learnMoreAndApplyNow(offerData?.design_version)}
          >
            {offerData?.cta_text}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderNaaV2 = () => {
    const { lockupImage } = props;
    const [offer] = offerData?.benefits;
    return (
      <div
        className="ReservationPathNaa__V2container"
        style={{ backgroundImage: `url(${offerData?.desktop_image_url})` }}
      >
        <div className="ReservationPathNaa__V2container__image">
          <img
            src={lockupImage}
            alt="Resy Amex Logo"
            className="resyAmexLogo"
          />
        </div>

        <div className="ReservationPathNaa__V2container__content">
          <div className="ReservationPathNaa__V2container__text">
            <h2 className="ReservationPathNaa__V2container__header">{offerData?.header}</h2>
            <p className="ReservationPathNaa__V2container__subheader">{offer?.display_text}</p>
          </div>

          <div className="ReservationPathNaa__V2container__footer">
            <Button
              className="cta-button"
              variant="primary"
              processing={prefillLoading}
              onClick={() => learnMoreAndApplyNow(offerData?.design_version)}
            >
              {offerData?.cta_text}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const displayNaaVersion = () => {
    if (offerData?.design_version && offerData?.design_version === 'v2') {
      return renderNaaV2();
    }
    return renderNAA();
  };

  return (
    <WidgetPage className="ReservationPathNaa" ariaLabel={getTranslation({ translationKey: 'reservation_header', fallback: 'Reservation Booked.' })}>
      {renderWidgetNav()}
      <OnScrollView
        className="ReservationPathNaa__ScrollView"
        scrollEvents={getScrollEvents()}
      >
        <WidgetPageHeader>{!showBackButton && renderWidgetTitle()}</WidgetPageHeader>
        <WidgetPageBody>{displayNaaVersion()}</WidgetPageBody>
      </OnScrollView>
    </WidgetPage>
  );
};

ReservationPathNaa.propTypes = {
  offerData: PropTypes.shape({
    header: PropTypes.string,
    image_url: PropTypes.string,
    subheader: PropTypes.string,
    benefits: PropTypes.arrayOf(PropTypes.shape({
      icon_url: PropTypes.string,
      display_text: PropTypes.string,
    })),
    annual_fee_value: PropTypes.string,
    terms_and_fees_url: PropTypes.string,
    benefits_terms_url: PropTypes.string,
    action_url: PropTypes.string,
    acquisition_offer_id: PropTypes.string,
    cta_text: PropTypes.string,
    design_version: PropTypes.string,
    desktop_image_url: PropTypes.string,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
    page: PropTypes.func,
  }).isRequired,
  navSearch: PropTypes.shape({
    seats: PropTypes.string,
    date: PropTypes.string,
  }).isRequired,
  venueName: PropTypes.string.isRequired,
  showBackButton: PropTypes.bool.isRequired,
  announcementId: PropTypes.string.isRequired,
  reservation: PropTypes.shape(reservationShape).isRequired,
  uuid: PropTypes.string.isRequired,
  lockupImage: PropTypes.string,
  setBookingFlow: PropTypes.func.isRequired,
};

ReservationPathNaa.defaultProps = {
  lockupImage: null,
};

ReservationPathNaa.displayName = 'ReservationPathNaa';
const mapStateToProps = (state) => ({
  navSearch: state.resyRouter.search,
  venueName: state.booking?.reservation?.venue?.venueName,
  reservation: state.booking?.reservation,
  offerData: state.naaAnnouncement.offerData,
  announcementId: state.naaAnnouncement?.id,
  showBackButton: state.naaAnnouncement?.showBackButton,
  uuid: state.resyConfig?.deviceId,
  lockupImage: state.naaAnnouncement?.lockupImage,
});

const mapDispatchToProps = (dispatch) => ({
  setBookingFlow: (data) => dispatch(setBookingFlowState(data)),
});

export { ReservationPathNaa as ComponentUnderTest };

export default connect(mapStateToProps, mapDispatchToProps)(ReservationPathNaa);
